import React, { useEffect, useState } from 'react';
import KpiCard from './KpiCard';
import OnboardingChecklist from './OnboardingChecklist';
import { useAuth } from '@/lib/auth/useAuth';
import { getApiKeys } from '@/api/api-keys';

const KpiCardsSection: React.FC = () => {
  const { user } = useAuth();
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(true);
  
  const [passRateValue, setPassRateValue] = useState("--");
  const [passRateTrend, setPassRateTrend] = useState(0);
  const [passRateProgress, setPassRateProgress] = useState(0);
  
  const [failedTestsValue, setFailedTestsValue] = useState("--");
  const [failedTestsTrend, setFailedTestsTrend] = useState(0);
  const [failedTestsProgress, setFailedTestsProgress] = useState(0);

  const [avgRuntimeValue, setAvgRuntimeValue] = useState("--");
  const [avgRuntimeTrend, setAvgRuntimeTrend] = useState(0);
  const [avgRuntimeProgress, setAvgRuntimeProgress] = useState(0);
  
  const [driftAlertsValue, setDriftAlertsValue] = useState("--");
  const [driftAlertsTrend, setDriftAlertsTrend] = useState(0);
  const [driftAlertsProgress, setDriftAlertsProgress] = useState(0);
  
  const [hasData, setHasData] = useState(true);

  useEffect(() => {
    // Check if user is first-time by checking API keys
    const checkFirstTimeUser = async () => {
      try {
        const response = await getApiKeys(user?.id);
        if (response.success && response.data.length > 0) {
          setIsFirstTimeUser(false);
          setShowOnboarding(false);
        }
      } catch (error) {
        // If API call fails, assume first-time user for onboarding
        setIsFirstTimeUser(true);
        setShowOnboarding(true);
      }
    };
    
    checkFirstTimeUser();
    
    // Fetch KPI data from API
    const fetchKpiData = async () => {
      try {
        const response = await fetch('/api/metrics/kpi');
        const data = await response.json();
        if (data.success) {
          setPassRateValue(data.passRate.value);
          setPassRateTrend(data.passRate.trend);
          setPassRateProgress(data.passRate.progressValue);
          
          setFailedTestsValue(data.failedTests.value);
          setFailedTestsTrend(data.failedTests.trend);
          setFailedTestsProgress(data.failedTests.progressValue);
          
          setAvgRuntimeValue(data.avgRuntime.value);
          setAvgRuntimeTrend(data.avgRuntime.trend);
          setAvgRuntimeProgress(data.avgRuntime.progressValue);
          
          setDriftAlertsValue(data.driftAlerts.value);
          setDriftAlertsTrend(data.driftAlerts.trend);
          setDriftAlertsProgress(data.driftAlerts.progressValue);
          
          // Check if we have actual data
          const hasActualData = 
            data.passRate.value !== "0%" && 
            data.failedTests.value !== "0" && 
            data.avgRuntime.value !== "0s" && 
            data.driftAlerts.value !== "0";
          
          setHasData(hasActualData);
        }
      } catch (error) {
        console.error('Error fetching KPI data:', error);
        // Set default values on error
        setPassRateValue("0%");
        setPassRateTrend(0);
        setPassRateProgress(0);
        
        setFailedTestsValue("0");
        setFailedTestsTrend(0);
        setFailedTestsProgress(0);
        
        setAvgRuntimeValue("0s");
        setAvgRuntimeTrend(0);
        setAvgRuntimeProgress(0);
        
        setDriftAlertsValue("0");
        setDriftAlertsTrend(0);
        setDriftAlertsProgress(0);
        
        setHasData(false);
      }
    };
    
    fetchKpiData();
  }, []);

  // Show onboarding checklist for first-time users
  if (showOnboarding && isFirstTimeUser) {
    return <OnboardingChecklist />;
  }

  if (!hasData) {
    return (
      <div className="bg-card rounded-lg p-6 border border-border shadow-sm mb-6">
        <div className="flex flex-col items-center text-center py-6">
          <div className="rounded-full bg-primary/10 p-4 mb-4">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary"
            >
              <path d="M14 4.5V14.5M10 4.5V14.5M8 14.5H16M6 19.5H18M5 8.5H7M17 8.5H19" 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"/>
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">No data to display yet</h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            Connect your testing framework to start tracking metrics and visualizing your test results.
          </p>
          <a 
            href="/integrations" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            Set up an integration
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <KpiCard 
        title="Pass Rate" 
        value={passRateValue}
        trend={passRateTrend} 
        color="bg-accent/20"
        progressValue={passRateProgress}
        filterLink="/runs?status=passed"
        tooltipText="Click to view passing runs"
      />
      <KpiCard 
        title="Failed Tests" 
        value={failedTestsValue}
        trend={failedTestsTrend} 
        color="bg-status-red/20"
        progressValue={failedTestsProgress}
        filterLink="/runs?status=failed"
        tooltipText="Click to see failing tests"
      />
      <KpiCard 
        title="Avg Runtime" 
        value={avgRuntimeValue} 
        trend={avgRuntimeTrend} 
        color="bg-status-yellow/20"
        progressValue={avgRuntimeProgress}
        tooltipText="Average test runtime"
      />
      <KpiCard 
        title="Drift Alerts" 
        value={driftAlertsValue} 
        trend={driftAlertsTrend} 
        color="bg-purple-500/20"
        progressValue={driftAlertsProgress}
        filterLink="/alerts?type=drift"
        tooltipText="Click to view drift alerts"
      />
    </div>
  );
};

export default KpiCardsSection;
